import React from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import { getDetailOfCaseInCRM } from 'features/common/helpers';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { computeDLocalFeeAmount, DLOCAL_FEE_RATE } from 'features/transaction-details/constants/dlocalCapture';

export interface DLocalCaptureDisclaimerModalProps {
	open: boolean;
	pendingAuthorization: number;
	coinCode: string;
	dealId: number;
	saleOrderId?: string | null;
	onConfirm: () => void;
	onClose: () => void;
}

export const DLocalCaptureDisclaimerModal = ({
	open,
	pendingAuthorization,
	coinCode,
	dealId,
	saleOrderId,
	onConfirm,
	onClose,
}: DLocalCaptureDisclaimerModalProps): JSX.Element => {
	const feeAmount = computeDLocalFeeAmount(pendingAuthorization);
	const pendingLabel = formatTransactionAmount(pendingAuthorization, coinCode);
	const feeLabel = formatTransactionAmount(feeAmount, coinCode);
	const feePercentLabel = `${(DLOCAL_FEE_RATE * 100).toLocaleString('es-UY', { maximumFractionDigits: 1 })}%`;
	const caseUrl = getDetailOfCaseInCRM(dealId);
	const saleOrderHref = saleOrderId ? `/salesorder/${saleOrderId}` : null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Cobro por DLocal</DialogTitle>
			<DialogContent>
				<Alert severity="warning" sx={{ mb: 2 }}>
					<Typography variant="body2" component="div">
						Vas a cobrar por DLocal <strong>{pendingLabel}</strong> (monto pendiente de autorizar).
					</Typography>
				</Alert>
				<Typography variant="body2" component="div" gutterBottom>
					Esto genera un costo del <strong>{feePercentLabel}</strong> sobre ese monto: <strong>{feeLabel}</strong>.
				</Typography>
				<Typography variant="body2" component="div" gutterBottom>
					Ese costo se verá como un <strong>negativo de −{feeLabel}</strong> en la{' '}
					{saleOrderHref ? (
						<Link href={saleOrderHref} target="_blank" rel="noopener noreferrer" underline="hover">
							orden de venta
						</Link>
					) : (
						<strong>orden de venta</strong>
					)}{' '}
					del{' '}
					<Link href={caseUrl} target="_blank" rel="noopener noreferrer" underline="hover">
						caso {dealId}
					</Link>
					.
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Impacta el <strong>margen de la venta</strong> y las <strong>comisiones del vendedor</strong>.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancelar</Button>
				<Button variant="contained" onClick={onConfirm}>
					Continuar con el cobro
				</Button>
			</DialogActions>
		</Dialog>
	);
};
